import type { Locale } from "@/i18n";
import { categorizeIngredient as categorize } from "@/lib/categorization/algorithm";
import { normalizeIngredientText } from "@/lib/categorization/ingredient-normalization";
import type { Category } from "@/lib/types";

/**
 * Categorize an ingredient using merged multilingual dictionary lookup.
 * Language-agnostic: accepts input in ANY supported language without restrictions.
 *
 * @param text - The ingredient text in any language (en, zh-CN, zh-TW, etc.)
 * @param locale - Optional locale hint for locale-specific categorization
 *                 If provided: uses locale-specific dictionary (preserves cultural differences)
 *                 If omitted: uses merged dictionary (language-agnostic)
 * @returns The category for the ingredient
 *
 * @example
 * // No locale - language agnostic (bilingual users can mix languages)
 * categorizeIngredient("chicken")  // "meat"
 * categorizeIngredient("鸡肉")     // "meat"
 * categorizeIngredient("雞肉")     // "meat"
 * categorizeIngredient("干貝")     // "seafood"
 *
 * // With locale - preserves cultural differences
 * categorizeIngredient("桂圆", "zh-CN")  // "other" (dried longan)
 * categorizeIngredient("桂圓", "zh-TW")  // "fruit" (fresh longan)
 */
export function categorizeIngredient(text: string, locale?: Locale): Category {
  return categorize(text, locale);
}

/**
 * Normalize a quantity for display by:
 * 1. Converting Chinese numbers to Arabic (兩 → 2)
 * 2. Removing spaces in short English units (900 g → 900g)
 *
 * Used in UI for consistency in display.
 *
 * @param quantity - The quantity string (may contain Chinese numbers or spaces)
 * @returns Normalized quantity
 *
 * @example
 * normalizeQuantityForDisplay("兩顆") // "2顆"
 * normalizeQuantityForDisplay("三杯") // "3杯"
 * normalizeQuantityForDisplay("十一顆") // "11顆"
 * normalizeQuantityForDisplay("900 g") // "900g"
 * normalizeQuantityForDisplay("2 kg") // "2kg"
 * normalizeQuantityForDisplay("1 cup") // "1 cup" (long units keep space)
 */
export function normalizeQuantityForDisplay(quantity: string): string {
  // Chinese units pattern
  const chineseUnits =
    "克斤兩两磅公斤千克個个隻只條条根把片塊块包袋瓶罐盒聽听桶箱串顆颗粒枚支張张份碗盤盘碟杯勺匙壺壶鍋锅煲缸壇坛盆筐籃篮籮箩筒團团捲卷捆束紮扎掛挂副對对雙双套組组批群簇叢丛堆疊叠摞排行列";
  const chineseNumPattern = "[一二三四五六七八九十百千万壹貳贰參叁肆伍陸陆柒捌玖拾佰仟萬兩两零〇]";

  // 1. Chinese number + Chinese unit: normalize number to Arabic
  const pattern = new RegExp(`^(${chineseNumPattern}+)([${chineseUnits}]+)$`);
  const match = quantity.match(pattern);

  if (match) {
    const chineseNum = match[1];
    const unit = match[2];
    const arabicNum = chineseToArabic(chineseNum);
    return `${arabicNum}${unit}`;
  }

  // 2. Bare Chinese number (no unit): normalize to Arabic
  const barePattern = new RegExp(`^${chineseNumPattern}+$`);
  if (barePattern.test(quantity)) {
    return chineseToArabic(quantity);
  }

  // 3. English: Remove space between number and abbreviated units
  // Short units: g, kg, lb, oz, ml, l, tbsp, tsp
  // Keep space for long units (cup, cups, tablespoon, etc.)
  const shortUnitPattern = /^(\d+\.?\d*)\s+(g|kg|lb|lbs|oz|ml|l|tbsp|tsp)$/i;
  const shortUnitMatch = quantity.match(shortUnitPattern);
  if (shortUnitMatch) {
    return `${shortUnitMatch[1]}${shortUnitMatch[2]}`;
  }

  // No normalization needed, return as-is
  return quantity;
}

/**
 * Convert Chinese numbers to Arabic numerals
 * Exported for UI display normalization (兩 → 2)
 *
 * @example
 * chineseToArabic("一") // "1"
 * chineseToArabic("兩") // "2"
 * chineseToArabic("三") // "3"
 * chineseToArabic("十") // "10"
 * chineseToArabic("二十三") // "23"
 */
export function chineseToArabic(chineseNum: string): string {
  const map: Record<string, number> = {
    零: 0,
    〇: 0,
    一: 1,
    壹: 1,
    二: 2,
    兩: 2,
    两: 2,
    貳: 2,
    贰: 2,
    三: 3,
    參: 3,
    叁: 3,
    四: 4,
    肆: 4,
    五: 5,
    伍: 5,
    六: 6,
    陸: 6,
    陆: 6,
    七: 7,
    柒: 7,
    八: 8,
    捌: 8,
    九: 9,
    玖: 9,
    十: 10,
    拾: 10,
    百: 100,
    佰: 100,
    千: 1000,
    仟: 1000,
    万: 10000,
    萬: 10000,
  };

  // Simple single digit
  if (chineseNum.length === 1 && map[chineseNum] !== undefined) {
    return map[chineseNum].toString();
  }

  // Handle complex numbers like 二十三, 五百, etc.
  let result = 0;
  let current = 0;
  let temp = 0;

  for (let i = 0; i < chineseNum.length; i++) {
    const char = chineseNum[i];
    const value = map[char];

    if (value === undefined) return chineseNum; // Not a valid Chinese number

    if (value >= 10) {
      // Multiplier (十, 百, 千, 万)
      if (temp === 0) temp = 1; // 十 = 1 * 10
      if (value === 10000) {
        result += (current + temp) * value;
        current = 0;
        temp = 0;
      } else {
        current += temp * value;
        temp = 0;
      }
    } else {
      // Digit (一, 二, 三, etc.)
      temp = value;
    }
  }

  return (result + current + temp).toString();
}

/**
 * Parse quantity from ingredient text and normalize it.
 *
 * Process:
 * 1. Detect quantity pattern (number + optional unit)
 * 2. Normalize Chinese numbers to Arabic (兩 → 2, 三 → 3)
 * 3. Return normalized quantity
 *
 * @param text - The ingredient text
 * @returns Object with { quantity, normalizedQuantity } or undefined
 *
 * @example
 * parseQuantity("2蘋果")       // { quantity: "2", normalizedQuantity: "2" }
 * parseQuantity("兩顆蘋果")    // { quantity: "兩顆", normalizedQuantity: "2顆" }
 * parseQuantity("500g chicken") // { quantity: "500g", normalizedQuantity: "500g" }
 * parseQuantity("三杯 雞")     // { quantity: "三杯", normalizedQuantity: "3杯" }
 */
export function parseQuantity(text: string): string | undefined {
  // Chinese quantity patterns - check FIRST (before English numeric patterns)
  // Supports BOTH Simplified and Traditional Chinese characters

  // Common Chinese units (measure words) - includes both TC and SC variants
  const chineseUnits =
    "克斤兩两磅公斤千克個个隻只條条根把片塊块包袋瓶罐盒聽听桶箱串顆颗粒枚支張张份碗盤盘碟杯勺匙壺壶鍋锅煲缸壇坛盆筐籃篮籮箩筒團团捲卷捆束紮扎掛挂副對对雙双套組组批群簇叢丛堆疊叠摞排行列打";

  // Pattern 1: Chinese number + Chinese unit: "三杯", "兩顆", "一斤"
  const chineseNumPattern = "[一二三四五六七八九十百千万壹貳贰參叁肆伍陸陆柒捌玖拾佰仟萬兩两零〇]";
  const chineseWithUnitPattern = new RegExp(`^(${chineseNumPattern}+[${chineseUnits}]+)\\s*`);
  const chineseWithUnitMatch = text.match(chineseWithUnitPattern);
  if (chineseWithUnitMatch) {
    // Return as-is: "兩顆", "三杯" (don't normalize - keep user's input)
    return chineseWithUnitMatch[1];
  }

  // Pattern 2: Arabic number + Chinese unit: "5條", "500克", "2顆"
  const arabicWithChineseUnitPattern = new RegExp(`^([0-9]+\\.?[0-9]*[${chineseUnits}]+)\\s*`);
  const arabicWithChineseUnitMatch = text.match(arabicWithChineseUnitPattern);
  if (arabicWithChineseUnitMatch) {
    return arabicWithChineseUnitMatch[1].trim();
  }

  // Pattern 3: Bare Arabic number + Chinese text (no unit): "2蘋果", "5魚"
  // Need to add implicit unit based on item type
  const bareArabicPattern = /^([0-9]+\.?[0-9]*)\s*([一-龥]+)/;
  const bareArabicMatch = text.match(bareArabicPattern);
  if (bareArabicMatch) {
    return bareArabicMatch[1].trim(); // Return just the number
  }

  // Pattern 4: Chinese number + Chinese text (no explicit unit): "三蘋果", "兩魚"
  const bareChinesePattern = new RegExp(`^(${chineseNumPattern}+)\\s*([一-龥]+)`);
  const bareChineseMatch = text.match(bareChinesePattern);
  if (bareChineseMatch) {
    // Return as-is: "兩", "三" (don't normalize - keep user's input)
    return bareChineseMatch[1].trim();
  }

  // English quantity patterns
  const patterns = [
    // Fractions with units: "1/2 cup", "3/8 lb"
    /^(\d+\/\d+\s+(?:lb|lbs|g|kg|oz|ounce|ounces|pound|pounds|gram|grams|kilogram|kilograms|cup|cups|tablespoon|tablespoons|tbsp|teaspoon|teaspoons|tsp|ml|l|liter|liters|bottle|bottles|can|cans|jar|jars))\s+/i,
    // Numeric with units and "of": "1 bottle of", "2 cans of"
    /^(\d+\.?\d*\s+(?:bottle|bottles|can|cans|jar|jars))\s+(?:of\s+)?/i,
    // Numeric with unit (with or without space): "1lb", "450g", "1.25 kg"
    /^(\d+\.?\d*\s*(?:lb|lbs|g|kg|oz|ounce|ounces|pound|pounds|gram|grams|kilogram|kilograms))\s+(?:of\s+)?/i,
    // Numeric with volume (with or without space): "2 cups", "3 tablespoons", "2tbsp", "3tsp"
    /^(\d+\.?\d*\s*(?:cup|cups|tablespoon|tablespoons|tbsp|teaspoon|teaspoons|tsp|ml|l|liter|liters))\s+(?:of\s+)?/i,
    // Just fractions: "1/2", "1/4"
    /^(\d+\/\d+)\s+/,
    // Just numeric: "3", "5", "10" (bare numbers)
    /^(\d+\.?\d*)\s+/,
  ];

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match) {
      return match[1].trim();
    }
  }

  return undefined;
}

/**
 * Process ingredient text to remove quantity and "of" word.
 * Capitalizes only if there's no quantity.
 *
 * @param text - The ingredient text
 * @param quantity - The parsed quantity (if any)
 * @returns Processed ingredient text
 *
 * @example
 * processIngredientText("1 bottle of honey", "1 bottle") // "honey"
 * processIngredientText("beef") // "Beef"
 * processIngredientText("500g chicken", "500g") // "chicken"
 */
export function processIngredientText(text: string, quantity?: string): string {
  let processed = text;

  // If we have a quantity, remove it from the text
  if (quantity) {
    // Escape regex special characters in quantity
    const escapedQty = quantity.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

    // Match quantity at start, with optional whitespace and "of" (English pattern)
    // For Chinese: "2蘋果" → removes "2", leaves "蘋果"
    // For English: "2 apples" → removes "2 ", leaves "apples"
    // For English: "1 bottle of wine" → removes "1 bottle of ", leaves "wine"
    processed = text.replace(new RegExp(`^${escapedQty}\\s*(?:of\\s+)?`, "i"), "");

    // Do NOT capitalize when there's a quantity
    return processed.trim();
  }

  // Only capitalize when there's NO quantity
  processed = processed.trim();
  if (processed.length > 0) {
    processed = processed.charAt(0).toUpperCase() + processed.slice(1);
  }

  return processed;
}

/**
 * Parse bulk ingredient input into individual ingredient strings.
 * Handles multiple separators and formats.
 *
 * Normalization applied:
 * 1. Ingredient text normalization (conversational → structured format)
 * 2. Checkbox/bullet removal
 * 3. Numbered list removal
 * 4. Comma/period splitting
 *
 * @param text - Bulk text with multiple ingredients
 * @returns Array of individual ingredient strings
 *
 * @example
 * parseBulkIngredients("1 apple\n2 orange") // ["1 apple", "2 orange"]
 * parseBulkIngredients("• potato\n• lettuce") // ["potato", "lettuce"]
 * parseBulkIngredients("I need two dozen eggs") // ["2 dozen eggs"]
 * parseBulkIngredients("two apples three bananas") // ["2 apples", "3 bananas"]
 */
export function parseBulkIngredients(text: string): string[] {
  if (!text.trim()) return [];

  // Step 1: Normalize ingredient text (conversational → structured)
  // Handles "two apples three bananas" → "2 apples\n3 bananas"
  const normalized = normalizeIngredientText(text);

  // Step 2: Split by newlines
  const lines = normalized.split("\n");
  const processed: string[] = [];

  for (let line of lines) {
    // Remove checkbox markers: - [ ], - [x]
    line = line.replace(/^[\s-]*\[\s*[xX]?\s*\]\s*/g, "");

    // Remove numbered list markers: 1., 2., etc.
    line = line.replace(/^\s*\d+\.\s+/g, "");

    // Remove bullet points: •, -, *, ○, ●
    line = line.replace(/^[\s•\-*○●►▪▫■□]+\s*/g, "");

    // Split by commas and periods (but NOT periods in decimal numbers)
    // Use lookbehind and lookahead to avoid splitting decimal points like "0.5"
    // Match comma OR period that's NOT preceded by a digit AND NOT followed by a digit
    const items = line
      .split(/,|\.(?!\d)(?<!\d\.)/)
      .map((item) => item.trim())
      .filter((item) => item.length > 0);

    processed.push(...items);
  }

  return processed.filter((item) => item.length > 0);
}
